import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CheckCircle2, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { Dropdown } from '@shared/ui/primitives/Dropdown';
import { ActionButton } from '@shared/ui/forms/FormFields';
import { Stepper } from '@shared/ui/lists/Stepper';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { toApiError } from '@shared/api/errors';
import { setJoinTenantContext } from '@shared/api/joinTenantContext';
import { toast } from '@shared/ui/feedback/Toast';
import {
  completeInstance,
  completeStep,
  createFlowInstance,
  getFlowInstance,
  moveToNextStep,
  requestFlowOtp,
  updateItemValue,
  uploadFlowFiles,
  verifyFlowOtp,
} from './api';
import type { FlowInstance, FlowInstanceStep, FlowInstanceStepItem } from './types';

type Config = Record<string, unknown>;

const cfg = (item: FlowInstanceStepItem): Config => (item.item_config ?? {}) as Config;
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/**
 * Server-driven onboarding runner — flow-instance model (matches the live
 * Angular `OnboardingComponent`). Every step item is rendered by type:
 *   - content    → rich text / HTML block
 *   - question   → a valued input (text, select, checkbox, file, …)
 *   - action     → an interactive handler (e.g. OTP verification)
 *   - transition → the button that completes the step and advances the flow
 *
 * Route shapes:
 *   /onboarding/:id              → :id is a flow_template_id (create instance)
 *   /onboarding?template=…       → flow_template_id via query
 *   /onboarding?instance=…       → resume an existing instance
 *   /onboarding?onboardingId=…   → link an existing tenant onboarding row
 *
 * Embedded mode (access invite onboarding):
 *   embedInstanceId + onEmbedComplete — stay on parent page and finalize credentials there.
 */
export interface OnboardingEmbedProps {
  embedInstanceId?: string;
  onEmbedComplete?: () => void;
}

export function Onboarding({ embedInstanceId, onEmbedComplete }: OnboardingEmbedProps = {}): JSX.Element {
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const queryClient = useQueryClient();
  const embedCompleteFired = useRef(false);

  const instanceId = embedInstanceId ?? search.get('instance') ?? undefined;
  const flowTemplateId = paramId ?? search.get('template') ?? undefined;
  const onboardingId = search.get('onboardingId') ?? undefined;
  const flowContext = search.get('context') ?? undefined;
  const flowKind = search.get('kind') ?? undefined;
  const flowTitle = search.get('title') ?? undefined;
  const flowBackTo = search.get('backTo') ?? undefined;
  // When launched from the "join another community" flow, keep addressing the
  // target tenant (X-Tenant-ID) for every flow-instance call. Set synchronously
  // so it's active before react-query fires; cleared on unmount.
  const joinTenantId = search.get('joinTenant') ?? undefined;
  if (joinTenantId) setJoinTenantContext(joinTenantId);
  useEffect(() => {
    return () => {
      if (joinTenantId) setJoinTenantContext(null);
    };
  }, [joinTenantId]);

  /* Leave the flow — drop the join-tenant override and return to the app.
     (For a post-login required-onboarding gate the gate may route the member
     back; for the join flow it returns them to their own community.) */
  const handleExit = () => {
    if (joinTenantId) setJoinTenantContext(null);
    navigate('/app/home');
  };
  const isSurveyFlow = flowContext === 'survey';
  const handleSurveyBack = () => {
    if (joinTenantId) setJoinTenantContext(null);
    const idx = typeof window !== 'undefined' ? window.history.state?.idx : undefined;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
      return;
    }
    navigate(flowBackTo || '/app/surveys');
  };
  const queryKey = ['flow-instance', instanceId ?? flowTemplateId ?? 'default', onboardingId];

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const instanceQuery = useQuery<FlowInstance>({
    queryKey,
    queryFn: async () => {
      if (instanceId) return getFlowInstance(instanceId);
      return createFlowInstance({ flow_template_id: flowTemplateId, onboarding_id: onboardingId });
    },
  });

  const instance = instanceQuery.data;
  const steps = useMemo(() => instance?.steps ?? [], [instance]);
  const activeStep = useMemo<FlowInstanceStep | undefined>(
    () => steps.find((s) => s.status === 'active') ?? steps.find((s) => s.status === 'pending'),
    [steps],
  );
  const stepIndex = activeStep ? steps.indexOf(activeStep) : steps.length;
  const done = instance?.status === 'completed';

  useEffect(() => {
    if (!embedInstanceId || !onEmbedComplete || !done || embedCompleteFired.current) return;
    embedCompleteFired.current = true;
    onEmbedComplete();
  }, [embedInstanceId, onEmbedComplete, done]);

  useEffect(() => {
    if (!done || !isSurveyFlow) return;
    void queryClient.invalidateQueries({ queryKey: ['surveys-my'] });
    void queryClient.invalidateQueries({ queryKey: ['surveys-available'] });
  }, [done, isSurveyFlow, queryClient]);

  /* Seed local values from the instance whenever the active step (re)loads. */
  useEffect(() => {
    if (!activeStep?.items) return;
    setValues((prev) => {
      const next = { ...prev };
      for (const item of activeStep.items ?? []) {
        if (item.item_value !== undefined && next[item.instance_item_id] === undefined) {
          next[item.instance_item_id] = item.item_value;
        }
      }
      return next;
    });
  }, [activeStep]);

  /* A transition item drives the flow: persist answers, complete the step,
     advance — and finalise the instance once no active step remains. */
  const advance = useMutation({
    mutationFn: async () => {
      if (!instance || !activeStep) return undefined;
      const missingRequired = getMissingRequiredItems(activeStep.items ?? [], values);
      if (missingRequired.length > 0) {
        throw new Error(
          `Complete required fields before continuing: ${missingRequired
            .map((item) => item.name)
            .join(', ')}`,
        );
      }
      const invalidItems = (activeStep.items ?? [])
        .filter((item) => item.item_type === 'question' && item.is_available)
        .map((item) => ({
          item,
          messages: getClientValidationMessages(item, values[item.instance_item_id]),
        }))
        .filter(({ messages }) => messages.length > 0);
      if (invalidItems.length > 0) {
        throw new Error(invalidItems[0]?.messages[0] ?? 'Please review the highlighted fields.');
      }
      const questionItems = (activeStep.items ?? []).filter(
        (i) => i.item_type === 'question' && i.is_available,
      );
      for (const item of questionItems) {
        const value = values[item.instance_item_id];
        if (value !== undefined && value !== '') {
          await updateItemValue(instance.instance_id, item.instance_item_id, value);
        }
      }
      await completeStep(instance.instance_id, activeStep.instance_step_id);
      await moveToNextStep(instance.instance_id);
      const refreshed = await getFlowInstance(instance.instance_id);
      const hasNavigableStep = refreshed.steps?.some(
        (step) => step.status === 'active' || step.status === 'pending',
      );
      if (refreshed.status !== 'completed' && !hasNavigableStep) {
        await completeInstance(instance.instance_id);
        return getFlowInstance(instance.instance_id);
      }
      return refreshed;
    },
    onSuccess: (refreshed) => {
      setError(null);
      if (refreshed) queryClient.setQueryData(queryKey, refreshed);
    },
    onError: (e) => {
      const apiError = toApiError(e);
      if (isMissingFlowResourceError(apiError.message)) {
        const title = 'This form session expired';
        const description = 'Please reopen the survey or eForm and try again.';
        setError(description);
        toast.warning(title, description);
        if (isSurveyFlow) handleSurveyBack();
        else navigate('/app/home', { replace: true });
        return;
      }
      setError(apiError.message);
    },
  });

  if (instanceQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-32" />
        <Skeleton className="h-11" />
      </div>
    );
  }
  if (instanceQuery.isError || !instance) {
    return <ErrorState onRetry={() => instanceQuery.refetch()} />;
  }

  const branding = instance.onboarding_branding;
  const compactSurveyTitle = resolveCompactSurveyTitle(
    flowTitle,
    instance.name,
    activeStep?.name,
    flowKind,
  );

  if (done) {
    if (onEmbedComplete) {
      return (
        <div className="flex flex-col items-center gap-4 py-6">
          <Skeleton className="h-8 w-48" />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <CheckCircle2 className="h-12 w-12 text-success" strokeWidth={1.5} />
        <h1 className="display-serif text-display-lg text-ink">
          {branding?.title ?? "You're all set"}
        </h1>
        <p className="text-body text-ink-secondary">
          {branding?.message ?? 'Your onboarding is complete.'}
        </p>
        <ActionButton className="mt-2" onClick={() => navigate('/app/home')}>
          Continue
        </ActionButton>
      </div>
    );
  }

  const items = [...(activeStep?.items ?? [])]
    .filter((i) => i.is_available)
    .sort((a, b) => a.sequence_number - b.sequence_number);
  const transitionItems = items.filter((item) => item.item_type === 'transition');
  const nonTransitionItems = items.filter((item) => item.item_type !== 'transition');
  const missingRequiredItems = getMissingRequiredItems(nonTransitionItems, values);
  const validationMessagesByItemId = new Map(
    nonTransitionItems.map((item) => [
      item.instance_item_id,
      getClientValidationMessages(item, values[item.instance_item_id]),
    ]),
  );
  const hasBlockingValidationIssues = Array.from(validationMessagesByItemId.values()).some(
    (messages) => messages.length > 0,
  );
  const availableSteps = steps.filter((step) => step.is_available !== false);
  const isLastAvailableStep = Boolean(
    activeStep &&
      availableSteps[availableSteps.length - 1]?.instance_step_id === activeStep.instance_step_id,
  );

  const setValue = (id: string, v: unknown) => {
    setError(null);
    setValues((prev) => ({ ...prev, [id]: v }));
  };

  return (
    <div className="flex flex-col gap-5">
      {isSurveyFlow ? (
        <div
          className="sticky top-0 z-20 overflow-hidden rounded-b-sm bg-primary-900 px-5 pt-safe-or-3 pb-3 backdrop-blur-xl backdrop-saturate-150 shadow-[inset_0_-1px_0_rgba(255,255,255,0.10),0_12px_30px_-18px_rgba(0,0,0,0.55)]"
          style={{
            marginLeft: '-1.5rem',
            marginRight: '-1.5rem',
            marginTop: 'calc(-1 * max(24px, env(safe-area-inset-top)))',
          }}
        >
          <div className="grid min-h-9 grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-3">
            <Button
              type="button"
              onClick={handleSurveyBack}
              aria-label="Back to survey"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full border border-white/10 bg-white/12 text-white hover:bg-white/18"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            </Button>
            <h1 className="min-w-0 truncate text-left font-display text-body font-semibold leading-6 text-white">
              {compactSurveyTitle}
            </h1>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleExit}
          className="self-end inline-flex items-center gap-1 rounded-pill px-2 py-1 text-caption text-ink-muted transition-colors hover:text-ink"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
          Exit
        </button>
      )}

      {steps.length > 1 ? (
        <Stepper total={steps.length} current={stepIndex} labels={steps.map((s) => s.name)} />
      ) : null}

      <header>
        <span className="eyebrow">{branding?.title ?? 'Onboarding'}</span>
        <h1 className="display-serif text-display-lg text-ink mt-1">
          {activeStep?.name ?? 'Welcome'}
        </h1>
        {activeStep?.description ? (
          <p className="text-body text-ink-secondary mt-1">{activeStep.description}</p>
        ) : null}
      </header>

      {error ? <Banner variant="danger" title={error} /> : null}

      <div className="flex flex-col gap-5">
        {nonTransitionItems.map((item) => {
          return (
            <OnboardingItem
              key={item.instance_item_id}
              item={item}
              instanceId={instance.instance_id}
              value={values[item.instance_item_id]}
              validationMessages={validationMessagesByItemId.get(item.instance_item_id) ?? []}
              onChange={(v) => setValue(item.instance_item_id, v)}
              onActionDone={() => queryClient.invalidateQueries({ queryKey })}
            />
          );
        })}

        {transitionItems.map((item) => {
          const c = cfg(item);
          const variant = c.button_style === 'secondary' ? 'secondary' : 'primary';
          return (
            <Button
              key={item.instance_item_id}
              width="full"
              variant={variant}
              disabled={missingRequiredItems.length > 0 || hasBlockingValidationIssues}
              loading={advance.isPending}
              onClick={() => advance.mutate()}
            >
              {str(c.button_label) ?? item.name ?? 'Continue'}
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          );
        })}

        {transitionItems.length === 0 ? (
          <Button
            width="full"
            disabled={missingRequiredItems.length > 0 || hasBlockingValidationIssues}
            loading={advance.isPending}
            onClick={() => advance.mutate()}
          >
            {isLastAvailableStep ? 'Submit' : 'Next step'}
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        ) : null}

        {missingRequiredItems.length > 0 ? (
          <p className="text-caption text-ink-muted">
            Complete all required fields before continuing.
          </p>
        ) : hasBlockingValidationIssues ? (
          <p className="text-caption text-ink-muted">
            Review the highlighted fields before continuing.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function resolveCompactSurveyTitle(
  explicitTitle: string | undefined,
  instanceName: string | undefined,
  stepName: string | undefined,
  flowKind: string | undefined,
): string {
  const clean = (value: string | undefined): string => String(value ?? '').replace(/^Response:\s*/i, '').trim();

  const title = clean(explicitTitle) || clean(instanceName) || clean(stepName);
  if (title) return title;
  return flowKind === 'eform' ? 'eForm' : 'Survey';
}

function isMissingFlowResourceError(message: string | undefined): boolean {
  const normalized = String(message ?? '').toLowerCase();
  return normalized.includes('item not found') || normalized.includes('instance not found');
}

function getMissingRequiredItems(
  items: FlowInstanceStepItem[],
  values: Record<string, unknown>,
): FlowInstanceStepItem[] {
  return items.filter((item) => {
    if (item.is_available === false) return false;
    const config = cfg(item);
    const actionParams = asObject(config.action_params);
    const isRequired =
      config.is_required === true ||
      config.required === true ||
      actionParams.is_required === true ||
      actionParams.required === true;

    if (!isRequired) return false;
    return !isItemSatisfied(item, values[item.instance_item_id]);
  });
}

function getClientValidationMessages(item: FlowInstanceStepItem, draftValue: unknown): string[] {
  if (item.item_type !== 'question' || item.is_available === false) {
    return [];
  }

  const config = cfg(item);
  const rules = asObject(config.validation_rules);
  const inputType = String(config.input_type ?? config.type ?? 'text');
  const value = draftValue ?? item.item_value;

  if (value === null || value === undefined || value === '') {
    return [];
  }

  const messages: string[] = [];

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const minLength = asNumber(rules.min_length);
    const maxLength = asNumber(rules.max_length);
    const pattern = str(rules.pattern);

    if (typeof minLength === 'number' && trimmed.length < minLength) {
      messages.push(`Please enter at least ${minLength} characters.`);
    }
    if (typeof maxLength === 'number' && trimmed.length > maxLength) {
      messages.push(`Please keep this answer under ${maxLength} characters.`);
    }
    if (pattern) {
      try {
        const regex = new RegExp(pattern);
        if (!regex.test(trimmed)) {
          messages.push('Please match the required format.');
        }
      } catch {
        // Ignore invalid authored patterns on the client; the backend remains authoritative.
      }
    }
  }

  if (inputType === 'number' || typeof value === 'number') {
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isNaN(numericValue)) {
      const min = asNumber(rules.min);
      const max = asNumber(rules.max);
      if (typeof min === 'number' && numericValue < min) {
        messages.push(`Please enter a value of at least ${min}.`);
      }
      if (typeof max === 'number' && numericValue > max) {
        messages.push(`Please enter a value of ${max} or less.`);
      }
    }
  }

  return messages;
}

function isItemSatisfied(item: FlowInstanceStepItem, draftValue: unknown): boolean {
  if (item.item_type === 'content' || item.item_type === 'section' || item.item_type === 'transition') {
    return true;
  }

  if (item.item_type === 'action') {
    return item.status === 'completed';
  }

  if (item.status === 'completed') {
    return true;
  }

  const config = cfg(item);
  const inputType = String(config.input_type ?? config.type ?? 'text');
  const value = draftValue ?? item.item_value;

  if (inputType === 'checkbox' || inputType === 'boolean') {
    return typeof value === 'boolean';
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
}

function asObject(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function getQuestionHelperText(item: FlowInstanceStepItem, value: unknown): string | undefined {
  const rules = asObject(cfg(item).validation_rules);
  const minLength = asNumber(rules.min_length);
  const maxLength = asNumber(rules.max_length);
  if (typeof minLength !== 'number' && typeof maxLength !== 'number') {
    return undefined;
  }

  const length = typeof value === 'string' ? value.trim().length : 0;
  if (typeof minLength === 'number' && typeof maxLength === 'number') {
    return length > 0
      ? `${length} characters entered. Keep between ${minLength} and ${maxLength} characters.`
      : `Enter between ${minLength} and ${maxLength} characters.`;
  }
  if (typeof minLength === 'number') {
    return length > 0
      ? `${length} / ${minLength} characters minimum`
      : `Enter at least ${minLength} characters.`;
  }
  return length > 0
    ? `${length} / ${maxLength} characters`
    : `Keep this answer under ${maxLength} characters.`;
}

/* ── Item dispatcher ─────────────────────────────────────────────────── */

function OnboardingItem({
  item,
  instanceId,
  value,
  validationMessages,
  onChange,
  onActionDone,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  value: unknown;
  validationMessages: string[];
  onChange: (value: unknown) => void;
  onActionDone: () => void;
}): JSX.Element | null {
  if (item.item_type === 'content' || item.item_type === 'section') {
    return <ContentItem item={item} />;
  }
  if (item.item_type === 'action') {
    return <ActionItem item={item} instanceId={instanceId} onDone={onActionDone} />;
  }
  if (item.item_type === 'question') {
    return (
      <QuestionItem
        item={item}
        instanceId={instanceId}
        value={value}
        validationMessages={validationMessages}
        onChange={onChange}
      />
    );
  }
  return null;
}

/* ── Content ─────────────────────────────────────────────────────────── */

function ContentItem({ item }: { item: FlowInstanceStepItem }): JSX.Element | null {
  const c = cfg(item);
  const body = str(c.content_data) ?? str(c.body) ?? str(c.content);
  const isHtml = c.content_type === 'html';

  if (!body) {
    return item.name ? <p className="font-display text-headline text-ink">{item.name}</p> : null;
  }
  if (isHtml) {
    return (
      <div
        className="onboarding-content text-body text-ink-secondary [&_h1]:font-display [&_h1]:text-display-lg [&_h1]:text-ink [&_h2]:font-display [&_h2]:text-headline [&_h2]:text-ink [&_h3]:font-display [&_h3]:text-headline [&_h3]:text-ink [&_p]:mt-1.5 [&_a]:text-primary-700 [&_ul]:list-disc [&_ul]:pl-5"
        // Content is authored in the tenant's flow template (trusted admin input).
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }
  return <p className="text-body text-ink-secondary whitespace-pre-line">{body}</p>;
}

/* ── Question ────────────────────────────────────────────────────────── */

function QuestionItem({
  item,
  instanceId,
  value,
  validationMessages,
  onChange,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  value: unknown;
  validationMessages: string[];
  onChange: (value: unknown) => void;
}): JSX.Element {
  const c = cfg(item);
  const inputType = String(c.input_type ?? c.type ?? 'text');
  const placeholder = str(c.placeholder);
  const required = c.is_required === true;
  const options = Array.isArray(c.options)
    ? (c.options as Array<{ value: string; label: string }>)
    : [];
  const fieldId = `flow-${item.instance_item_id}`;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const validationMessage = validationMessages[0];
  const helperText = getQuestionHelperText(item, value);

  const inputClass =
    `rounded-sm bg-card hairline px-3 py-2.5 text-body text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
      validationMessage ? 'border border-danger/50 focus-visible:ring-danger' : ''
    }`;

  const label = (
    <span className="text-body-sm font-medium text-ink">
      {item.name}
      {required ? <span className="text-danger"> *</span> : null}
    </span>
  );

  /* ── File / image upload (single or multiple) ──────────────────────── */
  if (inputType === 'file' || inputType === 'image') {
    const allowMultiple = c.allow_multiple === true;
    const urls = Array.isArray(value)
      ? (value as string[])
      : typeof value === 'string' && value
        ? [value]
        : [];
    const count = urls.length;
    const accept = inputType === 'image' ? 'image/*' : undefined;
    return (
      <div className="flex flex-col gap-1.5">
        {label}
        <label
          htmlFor={fieldId}
          className="flex cursor-pointer items-center gap-2 rounded-sm border border-dashed border-hairline-strong bg-elevated px-3 py-3 text-body-sm text-ink-secondary"
        >
          {inputType === 'image' ? (
            <ImageIcon className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
          ) : (
            <Paperclip className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
          )}
          <span className="truncate">
            {uploading
              ? 'Uploading…'
              : count > 0
                ? `${count} ${count === 1 ? 'file' : 'files'} uploaded — add ${allowMultiple ? 'more' : 'another'}`
                : placeholder ?? (inputType === 'image' ? 'Choose image(s)' : 'Choose a file')}
          </span>
          <input
            id={fieldId}
            type="file"
            accept={accept}
            multiple={allowMultiple}
            className="hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              if (!files.length) return;
              setUploading(true);
              setUploadError(null);
              try {
                const uploaded = await uploadFlowFiles(instanceId, files);
                if (allowMultiple) onChange([...urls, ...uploaded]);
                else if (uploaded[0]) onChange(uploaded[0]);
              } catch (err) {
                setUploadError(err instanceof Error ? err.message : 'Upload failed');
              } finally {
                setUploading(false);
              }
            }}
          />
        </label>
        {uploadError ? <span className="text-caption text-danger">{uploadError}</span> : null}
        {!uploadError && validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </div>
    );
  }

  /* ── Radio (single choice) ─────────────────────────────────────────── */
  if (inputType === 'radio' && options.length > 0) {
    const current = String(value ?? '');
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 rounded-sm bg-card hairline px-3 py-2.5 text-body-sm text-ink"
            >
              <input
                type="radio"
                name={fieldId}
                value={o.value}
                checked={current === o.value}
                onChange={() => onChange(o.value)}
                className="h-4 w-4 accent-[color:var(--primary-600)]"
              />
              {o.label}
            </label>
          ))}
        </div>
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  /* ── Multiselect (multiple choice → string[]) ──────────────────────── */
  if (inputType === 'multiselect' && options.length > 0) {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (v: string) =>
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 rounded-sm bg-card hairline px-3 py-2.5 text-body-sm text-ink"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="h-4 w-4 accent-[color:var(--primary-600)]"
              />
              {o.label}
            </label>
          ))}
        </div>
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  return (
    <label htmlFor={fieldId} className="flex flex-col gap-1.5">
      {label}

      {inputType === 'textarea' ? (
        <textarea
          id={fieldId}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={placeholder}
          className={inputClass}
        />
      ) : (inputType === 'select' || inputType === 'dropdown') && options.length > 0 ? (
        <Dropdown
          id={fieldId}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{placeholder ?? 'Select…'}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Dropdown>
      ) : inputType === 'checkbox' || inputType === 'boolean' ? (
        <span className="flex items-center gap-2">
          <input
            id={fieldId}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 accent-[color:var(--primary-600)]"
          />
          <span className="text-body-sm text-ink-secondary">
            {str(c.checkbox_label) ?? 'Yes'}
          </span>
        </span>
      ) : (
        <input
          id={fieldId}
          type={
            inputType === 'email'
              ? 'email'
              : inputType === 'phone' || inputType === 'tel'
                ? 'tel'
                : inputType === 'number'
                  ? 'number'
                  : inputType === 'date'
                    ? 'date'
                    : 'text'
          }
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputClass}
        />
      )}

      {validationMessage ? (
        <span className="text-caption text-danger">{validationMessage}</span>
      ) : helperText ? (
        <span className="text-caption text-ink-muted">{helperText}</span>
      ) : null}
    </label>
  );
}

/* ── Action ──────────────────────────────────────────────────────────── */

function ActionItem({
  item,
  instanceId,
  onDone,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  onDone: () => void;
}): JSX.Element {
  const c = cfg(item);
  const actionType = String(c.action_type ?? c.action_handler ?? '');

  if (actionType === 'otpverify') {
    return <OtpAction item={item} instanceId={instanceId} onDone={onDone} />;
  }

  // Generic action — surfaced so the step isn't silently missing an item.
  return (
    <Card noGrain className="rounded-xl p-4">
      <p className="text-body-sm font-medium text-ink">{item.name}</p>
      <p className="mt-1 text-caption text-ink-muted">This step is handled by the estate office.</p>
    </Card>
  );
}

function OtpAction({
  item,
  instanceId,
  onDone,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  onDone: () => void;
}): JSX.Element {
  const c = cfg(item);
  const params = (c.action_params ?? {}) as Config;
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'idle' | 'sent' | 'verified'>(
    item.status === 'completed' ? 'verified' : 'idle',
  );
  const [err, setErr] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => requestFlowOtp(instanceId, item.instance_item_id),
    onSuccess: (res) => {
      setToken(String(res.token ?? ''));
      setPhase('sent');
      setErr(null);
    },
    onError: (e) => setErr(toApiError(e).message),
  });

  const verify = useMutation({
    mutationFn: () => verifyFlowOtp(instanceId, item.instance_item_id, token, code.trim()),
    onSuccess: () => {
      setPhase('verified');
      setErr(null);
      onDone();
    },
    onError: (e) => setErr(toApiError(e).message),
  });

  return (
    <Card noGrain className="rounded-xl p-4">
      <p className="text-body-sm font-medium text-ink">{item.name}</p>
      {str(params.help_text) ? (
        <p className="mt-1 text-caption text-ink-muted">{String(params.help_text)}</p>
      ) : null}

      {phase === 'verified' ? (
        <p className="mt-3 flex items-center gap-2 text-body-sm text-success">
          <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />
          Verified
        </p>
      ) : phase === 'sent' ? (
        <div className="mt-3 flex flex-col gap-2.5">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={str(params.code_label) ?? 'Verification code'}
            className="rounded-sm bg-elevated hairline px-3 py-2.5 text-body text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => send.mutate()}
              loading={send.isPending}
            >
              {str(params.send_button_label) ?? 'Resend code'}
            </Button>
            <Button
              width="full"
              disabled={code.trim().length < 4}
              loading={verify.isPending}
              onClick={() => verify.mutate()}
            >
              {str(params.verify_button_label) ?? 'Verify code'}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className="mt-3"
          variant="secondary"
          loading={send.isPending}
          onClick={() => send.mutate()}
        >
          {str(params.send_button_label) ?? 'Send code'}
        </Button>
      )}

      {err ? <p className="mt-2 text-caption text-danger">{err}</p> : null}
    </Card>
  );
}
